/**
 * The application chrome: the bar, and the navigation row under it.
 *
 * The bar carries the mark, a door into the command palette, and who is signed in.
 * Navigation is not in the bar: it is the row below, so a contract's own pages and the
 * reader's own pages sit on one line instead of half in the chrome and half in the page
 * body. On a contract route that row opens with the contract and its Structure and
 * Roster; everything after the divider is the reader's, wherever they are.
 */
import { useMemo, useState } from "preact/hooks";
import { LEAD_ROLES } from "@trellis/core";
import { Link } from "./link.js";
import { Menu } from "./menu.js";
import { openPalette } from "./palette.js";
import { applyTheme, readTheme, type Theme } from "../theme.js";
import type { Session } from "../auth.js";
import type { Route } from "../app.js";

export interface UnitHeld { programId: string; programName: string; unitId: string; unitName: string; kind: string; roleIds: string[] }
export interface Me { upn: string; name: string; managerUpn: string | null; timezone: string | null; isSponsor: boolean; portfolio: boolean; programs: { id: string; name: string; roleIds: string[] }[]; reports: { upn: string; name: string }[]; units?: UnitHeld[];
  /** What the row badges: work waiting on this person, and cadences that fall to them. */
  counts?: { needsYou: number; cadencesDue: number } }

export function canSeePrograms(me: Me | undefined): boolean {
  return !!me && (me.isSponsor || me.programs.some((p) => p.roleIds.length > 0));
}

export function canSeeUnits(me: Me | undefined): boolean {
  return !!me && (me.units?.length ?? 0) > 0;
}

/**
 * Operations answers to the sponsor for the whole tenant, and to a lead or program admin
 * for the contracts they hold: a backlog waiting on a configuration they own is theirs to
 * clear. A unit role opens nothing here, the way it opens nothing else above the unit.
 */
export function canSeeOps(me: Me | undefined): boolean {
  return !!me && (me.isSponsor || me.programs.some((p) => p.roleIds.some((r) => LEAD_ROLES.includes(r))));
}

/**
 * A contract's own pages answer to someone who holds a role on it, or to the sponsor.
 * A unit role opens the unit's week and nothing above it, so offering a unit lead
 * Structure or Roster is offering them a 403.
 */
export function canOpenProgram(me: Me | undefined, id: string): boolean {
  if (!me) return false;
  return me.isSponsor || (me.programs.find((p) => p.id === id)?.roleIds.length ?? 0) > 0;
}

/** The contract a route is inside, if it is inside one. */
export function contractOf(route: Route): string | undefined {
  switch (route.kind) {
    case "program": case "structure": case "model": case "roster-page": case "unit-week": case "draft": return route.id;
    default: return undefined;
  }
}

/**
 * A role id reads as its own name once the hyphens go: `delivery-lead` is "Delivery lead"
 * in the model too. `/api/me` sends ids and not names, and a second fetch of the model
 * just to caption the bar is not worth a request; `web.test.ts` holds the model to this.
 */
export function roleName(roleId: string): string {
  const words = roleId.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const RANK = ["delivery-lead", "deputy-delivery-lead", "program-admin", "technical-lead", "customer-contact"];

/**
 * What sits under the name: the role that carries the most authority, and where it is
 * held. A programme role always beats a unit role, and a unit role is captioned with its
 * unit: "Delivery lead · AGENCY.OIT.ORBIT" of someone who leads one workstream would
 * hand them the whole contract.
 */
export function standing(me: Me | undefined): string | undefined {
  if (!me) return undefined;
  const best =
    strongest(me.programs.flatMap((p) => p.roleIds.map((r) => ({ role: r, where: p.name }))))
    ?? strongest((me.units ?? []).flatMap((u) => u.roleIds.map((r) => ({ role: r, where: u.unitName }))));
  if (best) return `${roleName(best.role)} · ${best.where}`;
  if (me.isSponsor) return "Sponsor";
  if (me.portfolio) return "Portfolio lead";
  if (me.programs.length === 1) return me.programs[0]!.name;
  return undefined;
}

function strongest(held: { role: string; where: string }[]): { role: string; where: string } | undefined {
  return [...held].sort((a, b) => rank(a.role) - rank(b.role))[0];
}

function rank(roleId: string): number {
  const at = RANK.indexOf(roleId);
  return at === -1 ? RANK.length : at;
}

export function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts.length === 1 ? parts[0]!.slice(0, 2) : `${parts[0]![0]}${parts[parts.length - 1]![0]}`).toUpperCase();
}

export interface NavItem { href: string; label: string; on: boolean; count?: number; lead?: boolean; afterRule?: boolean }

/** A contract the chrome has been told the name of by the page that loaded it. */
export interface Contract { id: string; name: string }

/**
 * One row, in this order: the contract you are inside, then what is yours. `count` is set
 * only where the number is already in `/api/me` and is the number the page itself lists.
 */
export function navFor(route: Route, me: Me | undefined, contract?: Contract | undefined): NavItem[] {
  const items: NavItem[] = [];
  const id = contractOf(route);
  if (id && canOpenProgram(me, id)) {
    const at = `/app/programs/${encodeURIComponent(id)}`;
    const named = me?.programs.find((p) => p.id === id)?.name ?? (contract?.id === id ? contract.name : undefined);
    // A unit's week and a draft sit under the contract, so the contract is where you are.
    const here = route.kind === "program" || route.kind === "unit-week" || route.kind === "draft";
    items.push({ href: at, label: named ?? id, on: here, lead: true });
    items.push({ href: `${at}/structure`, label: "Structure", on: route.kind === "structure" });
    items.push({ href: `${at}/roster`, label: "Roster", on: route.kind === "roster-page" });
    items.push({ href: `${at}/model`, label: "Delivery model", on: route.kind === "model" });
  }
  const list = route.kind === "list" ? route.list : undefined;
  // The rule belongs between the two groups, so it is drawn only when there is a first
  // group to divide from: on a unit whose contract the reader cannot open there is none.
  items.push({ href: "/app", label: "Your record", on: (route.kind === "record" && !route.upn) || list === "commitments" || list === "candidates",
    ...(me?.counts?.needsYou ? { count: me.counts.needsYou } : {}), ...(items.length ? { afterRule: true } : {}) });
  // A zero badge is noise: the row says how many are waiting, or says nothing.
  items.push({ href: "/app/cadences", label: "Cadences", on: list === "cadences", ...(me?.counts?.cadencesDue ? { count: me.counts.cadencesDue } : {}) });
  if (canSeePrograms(me)) items.push({ href: "/app/programs", label: "Programs", on: route.kind === "programs" });
  // A unit's week belongs to the contract above it, unless the reader cannot open that,
  // in which case their own units are the section they are in.
  if (canSeeUnits(me)) items.push({ href: "/app/units", label: "Your units", count: me?.units?.length ?? 0,
    on: route.kind === "units" || (route.kind === "unit-week" && !canOpenProgram(me, route.id)) });
  if (me?.portfolio) items.push({ href: "/app/portfolio", label: "Portfolio", on: route.kind === "portfolio" });
  items.push({ href: "/app/initiatives", label: "Initiatives", on: route.kind === "initiatives" || route.kind === "initiative" });
  if (canSeeOps(me)) items.push({ href: "/app/ops", label: "Operations", on: route.kind === "ops" });
  if (me?.isSponsor) {
    items.push({ href: "/app/vips", label: "VIPs", on: route.kind === "vips" });
    items.push({ href: "/app/topics", label: "Topics", on: route.kind === "topics" });
    items.push({ href: "/app/integrations", label: "Instances", on: route.kind === "integrations" });
  }
  return items;
}

export function TopBar({ route, me, session, contract }: { route: Route; me: Me | undefined; session: Session; contract?: Contract | undefined }) {
  const items = useMemo(() => navFor(route, me, contract), [route, me, contract]);
  const inContract = !!contractOf(route);
  return (
    <>
      <header class="topbar">
        <Link href="/app" class="brand"><Mark /><span>Trellis</span></Link>
        <span class="grow" />
        <button type="button" class="search-btn" onClick={openPalette} aria-haspopup="dialog">
          Search <span class="kbd" aria-hidden="true">/</span>
        </button>
        <Identity me={me} session={session} />
      </header>
      <nav class="chrome-nav" aria-label="Sections">
        {items.map((i) => (
          <Link key={i.href} href={i.href} class={navClass(i)}>
            {i.label}
            {i.count === undefined ? null : <span class="count">{i.count}</span>}
          </Link>
        ))}
      </nav>
      <nav class="tabbar" aria-label="Primary">
        <Link href="/app" class={(route.kind === "record" && !route.upn) || route.kind === "list" ? "on" : ""}>Record</Link>
        {canSeePrograms(me) && <Link href="/app/programs" class={inContract || route.kind === "programs" ? "on" : ""}>Programs</Link>}
        {canSeeUnits(me) && <Link href="/app/units" class={route.kind === "units" || (route.kind === "unit-week" && !canSeePrograms(me)) ? "on" : ""}>Units</Link>}
        {me?.portfolio && <Link href="/app/portfolio" class={route.kind === "portfolio" ? "on" : ""}>Portfolio</Link>}
      </nav>
    </>
  );
}

/** The reader's own items are ruled off from the contract's, so the row reads as two groups. */
function navClass(i: NavItem): string {
  const parts: string[] = [];
  if (i.on) parts.push("on");
  if (i.lead) parts.push("lead");
  if (i.afterRule) parts.push("after-rule");
  return parts.join(" ");
}

function Identity({ me, session }: { me: Me | undefined; session: Session }) {
  const under = standing(me);
  // Holds the avatar's footprint while /api/me is in flight, so the bar does not jump
  // sideways the moment it lands.
  if (!me) return <span class="who-empty" aria-hidden="true" />;
  return (
    <Menu
      label={`Signed in as ${me.name}`}
      triggerClass="who"
      trigger={<>
        <span class="avatar" aria-hidden="true">{initialsOf(me.name)}</span>
        <span class="who-text"><b>{me.name}</b>{under ? <small>{under}</small> : null}</span>
      </>}
    >
      <p class="menu-label">Signed in as {me.upn}</p>
      <Link href="/app" class="menu-item">Your record</Link>
      <p class="menu-label">Appearance</p>
      <div class="menu-row"><span>Theme</span><ThemeToggle /></div>
      {session.signOut && <button type="button" class="menu-item" onClick={() => void session.signOut!()}>Sign out</button>}
    </Menu>
  );
}

const THEMES: { id: Theme; label: string }[] = [
  { id: "system", label: "Auto" },
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
];

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const pick = (t: Theme) => { applyTheme(t); setTheme(t); };
  return (
    <span class="seg theme-pick" role="group" aria-label="Colour theme">
      {THEMES.map((t) => (
        <button key={t.id} type="button" class={t.id === theme ? "on" : ""} aria-pressed={t.id === theme} onClick={() => pick(t.id)}>{t.label}</button>
      ))}
    </span>
  );
}

function Mark() {
  return <img src="/app/mark.png" alt="" width="26" height="26" />;
}
